'use client'

import { addHours } from 'date-fns'
import { useSearchParams } from 'next/navigation'
import React, { useState } from 'react'
import { useEffectOnce } from 'react-use'

import { cookies } from '@/libs/cookie'

import { IProduct } from '../../types/schema'

import '@/styles/components/_eGift_Cart_ProductCard.scss'
import { useTranslations } from 'next-intl'

interface ProductCardProps {
  product: IProduct
  buy_gift_url?: string | null
  index: number
}

//__HELPER
const FormatPrice = (price: number): string => {
  const formattedPrice = price.toFixed(2)
  const [integerPart, decimalPart] = formattedPrice.split('.')
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${formattedInteger}.${decimalPart}`
}

const FormatDate = (date: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit'
  }
  return new Date(date).toLocaleDateString('en-US', options)
}
const ProductCard: React.FC<ProductCardProps> = ({ product, buy_gift_url, index }) => {

  const t = useTranslations()
  const searchParams = useSearchParams()
  const [cartUrl, setCartUrl] = useState('#') // Initialize with placeholder

  // __EFFECT's - Handle UTM parameters and set cart URL
  useEffectOnce(() => {
    // Handle UTM source
    const utmSource = searchParams.get('utm_source')
    if (utmSource && !cookies.get('source')) {
      cookies.set('source', utmSource, {
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
        expires: addHours(new Date(), 1),
        path: '/',
      })
    }

    // Handle UTM content - same rule as utm_source: first value wins, 1 hour, cut to 10 chars
    const utmContent = searchParams.get('utm_content')
    if (utmContent && !cookies.get('utm_content')) {
      cookies.set('utm_content', Array.from(utmContent).slice(0, 10).join(''), {
        domain: process.env.NEXT_PUBLIC_COOKIE_DOMAIN,
        expires: addHours(new Date(), 1),
        path: '/',
      })
    }

    // Set cart URL on client side only
    if (typeof window !== 'undefined') {
      const baseUrl = `${window.location.origin}/en/onlinegift/cart/?gift_id=${product.pd_id}`
      // Re-get both UTM values to ensure they're available for URL building
      const currentUtmSource = searchParams.get('utm_source')
      const currentUtmContent = searchParams.get('utm_content')
      // URLSearchParams encodes the values and keeps the separator right when only one is present
      const utmParams = new URLSearchParams()
      if (currentUtmSource) utmParams.set('utm_source', currentUtmSource)
      if (currentUtmContent) utmParams.set('utm_content', currentUtmContent)
      const utmQuery = utmParams.toString()
      const finalUrl = utmQuery ? `${baseUrl}&${utmQuery}` : baseUrl
      setCartUrl(finalUrl)
    }
  })

  return (
    <div key={`${product.id || product.pd_id}-${index}`} className='product-card'>
      {product.image_url && (
        <a href={cartUrl} className='product-card__image-link' target='_blank'>
          <div className='product-card__image-overlay'>
            <span className='product-card__overlay-text'>{t('Promotion.click_to_buy')}</span>
          </div>
          <img
            src={product.image_url}
            alt={product.pd_name}
            className='product-card__image'
            loading='lazy'
          />
        </a>
      )}

      <div className='product-header'>
        <h4 className='product-title'>{product.pd_name}</h4>
        {Number(product.hour_calculate) > 0 && (
          <div className='product-badge'>
            <svg
              className='time-icon'
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 24 24'
              width='24'
              height='24'>
              <path d='M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm-1-13h2v5h-2zm0 6h5v2h-5z' />
            </svg>
            <span>{product.hour_calculate}h</span>
          </div>
        )}
      </div>

      {product.pd_detail_gift && (<div
        dangerouslySetInnerHTML={{ __html: product.pd_detail_gift }}
        className='product-description'
      />)}
      <div className='product-details'>
        <div className='product-details__price-wrapper'>
          <p className='product-price'>{FormatPrice(Number(product.price))}</p>
        </div>
        <p className='product-validity'>
          {product.giftuse_type === 'amount' ?
            t('Promotion.valid_gift_expire_n_days', { n: product.d_expire || '0' })
            : t('Promotion.valid_gift_expire_period', {
              start: FormatDate(product.issue_date),
              end: FormatDate(product.expire_date)
            })
          }
        </p>
        <a
          href={cartUrl}
          className='buy-gift-button'
          target='_blank'>
          {t('Promotion.add_to_cart')}
        </a>
      </div>
    </div>
  )
}

export default ProductCard
